import React from "react";
import {
  IconAlignLeft,
  IconBlockquote,
  IconCalendar,
  IconCheckbox,
  IconChevronDown,
  IconHeading,
  IconHash,
  IconMinus,
  IconPageBreak,
  IconPhoto,
  IconPointer,
  IconSignature,
  IconTypography,
} from "@tabler/icons-react";

type FieldIcon = React.ComponentType<{ size?: number | string }>;
import {
  ChoiceConfig,
  DateConfig,
  DividerConfig,
  FieldType,
  FormTemplateField,
  FormTemplateFieldConfig,
  ImageUploadConfig,
  LongTextConfig,
  NumberConfig,
  PageBreakConfig,
  SectionDescriptionConfig,
  SectionHeaderConfig,
  ShortTextConfig,
  SignatureConfig,
} from "../../types/formTemplates";

export type FieldTypeGroup = "text" | "choice" | "media" | "layout";

export interface FieldTypeMeta {
  type: FieldType;
  label: string;
  description: string;
  icon: FieldIcon;
  group: FieldTypeGroup;
  defaultLabel: string;
  isLayoutOnly: boolean;
  defaultConfig: FormTemplateFieldConfig;
}

// Keep `FIELD_TYPE_META` in the same order as the sidebar palette in the UI.
export const FIELD_TYPE_META: Readonly<Record<FieldType, FieldTypeMeta>> = {
  short_text: {
    type: "short_text",
    label: "Short Text",
    description: "Single-line input",
    icon: IconTypography,
    group: "text",
    defaultLabel: "Short text question",
    isLayoutOnly: false,
    defaultConfig: {} as ShortTextConfig,
  },
  long_text: {
    type: "long_text",
    label: "Long Text",
    description: "Multi-line textarea",
    icon: IconAlignLeft,
    group: "text",
    defaultLabel: "Long text question",
    isLayoutOnly: false,
    defaultConfig: {} as LongTextConfig,
  },
  number: {
    type: "number",
    label: "Number",
    description: "Numeric input",
    icon: IconHash,
    group: "text",
    defaultLabel: "Number question",
    isLayoutOnly: false,
    defaultConfig: {} as NumberConfig,
  },
  date: {
    type: "date",
    label: "Date",
    description: "Calendar date picker",
    icon: IconCalendar,
    group: "text",
    defaultLabel: "Date question",
    isLayoutOnly: false,
    defaultConfig: {} as DateConfig,
  },
  multiple_choice: {
    type: "multiple_choice",
    label: "Multiple Choice",
    description: "Checkboxes — pick any",
    icon: IconCheckbox,
    group: "choice",
    defaultLabel: "Multiple choice question",
    isLayoutOnly: false,
    defaultConfig: {
      options: [
        { id: "opt_1", label: "Option 1" },
        { id: "opt_2", label: "Option 2" },
      ],
    } satisfies ChoiceConfig,
  },
  single_choice: {
    type: "single_choice",
    label: "Single Choice",
    description: "Radio buttons — pick one",
    icon: IconPointer,
    group: "choice",
    defaultLabel: "Single choice question",
    isLayoutOnly: false,
    defaultConfig: {
      options: [
        { id: "opt_1", label: "Option 1" },
        { id: "opt_2", label: "Option 2" },
      ],
    } satisfies ChoiceConfig,
  },
  dropdown: {
    type: "dropdown",
    label: "Dropdown",
    description: "Select from a list",
    icon: IconChevronDown,
    group: "choice",
    defaultLabel: "Dropdown question",
    isLayoutOnly: false,
    defaultConfig: {
      options: [
        { id: "opt_1", label: "Option 1" },
        { id: "opt_2", label: "Option 2" },
      ],
    } satisfies ChoiceConfig,
  },
  image_upload: {
    type: "image_upload",
    label: "Image Upload",
    description: "Attach one or more images",
    icon: IconPhoto,
    group: "media",
    defaultLabel: "Upload images",
    isLayoutOnly: false,
    defaultConfig: {} as ImageUploadConfig,
  },
  signature: {
    type: "signature",
    label: "Signature",
    description: "Collect a drawn signature",
    icon: IconSignature,
    group: "media",
    defaultLabel: "Signature",
    isLayoutOnly: false,
    defaultConfig: { signatory: "client" } satisfies SignatureConfig,
  },
  section_header: {
    type: "section_header",
    label: "Section Header",
    description: "Group fields under a title",
    icon: IconHeading,
    group: "layout",
    defaultLabel: "New section",
    isLayoutOnly: true,
    defaultConfig: { text: "New section" } satisfies SectionHeaderConfig,
  },
  section_description: {
    type: "section_description",
    label: "Section Description",
    description: "Paragraph of helper text",
    icon: IconBlockquote,
    group: "layout",
    defaultLabel: "",
    isLayoutOnly: true,
    defaultConfig: {
      text: "Describe this section for whoever is filling out the form.",
    } satisfies SectionDescriptionConfig,
  },
  divider: {
    type: "divider",
    label: "Divider",
    description: "Horizontal line break",
    icon: IconMinus,
    group: "layout",
    defaultLabel: "",
    isLayoutOnly: true,
    defaultConfig: {} satisfies DividerConfig,
  },
  page_break: {
    type: "page_break",
    label: "Page Break",
    description: "Start a new page in the form",
    icon: IconPageBreak,
    group: "layout",
    defaultLabel: "",
    isLayoutOnly: true,
    defaultConfig: {} satisfies PageBreakConfig,
  },
};

export const FIELD_TYPE_ORDER: FieldType[] = [
  "short_text",
  "long_text",
  "number",
  "date",
  "multiple_choice",
  "single_choice",
  "dropdown",
  "image_upload",
  "signature",
  "section_header",
  "section_description",
  "divider",
  "page_break",
];

export const FIELD_TYPE_GROUPS: { group: FieldTypeGroup; label: string }[] = [
  { group: "text", label: "Text" },
  { group: "choice", label: "Choices" },
  { group: "media", label: "Media" },
  { group: "layout", label: "Layout" },
];

/**
 * Build a fresh field object for the given type with default values. The
 * caller is responsible for assigning a unique id (typically crypto.randomUUID).
 */
export function createDefaultField(
  id: string,
  type: FieldType,
): FormTemplateField {
  const meta = FIELD_TYPE_META[type];
  return {
    id,
    type,
    label: meta.defaultLabel,
    required: false,
    actor: meta.isLayoutOnly ? null : "staff",
    layout: { colSpan: 12 },
    config: { ...(meta.defaultConfig as object) },
  } as FormTemplateField;
}

export const ACTOR_LABEL: Record<"staff" | "client", string> = {
  staff: "Staff",
  client: "Client",
};

// Mantine color keys for actor badges.
export const ACTOR_COLOR: Record<"staff" | "client", string> = {
  staff: "teal",
  client: "burntOrange",
};
